import { FC, useState, useMemo } from 'react';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useQuery } from 'react-query';
import { Grid, Paper } from '@material-ui/core';
import { GridColumns, GridCellParams, DataGrid } from '@material-ui/data-grid';
import { useStyles } from './styles';
import { observer } from 'mobx-react-lite';
import EmailIcon from '../../../assets/icons/EndUser/Email.svg';
import { getRowsPerPageOptions } from 'utils/pagination';
import { Search } from 'components/Search';
import IconButton from '@material-ui/core/IconButton';
import { Button } from '@material-ui/core';
import HomeWorkIcon from '@material-ui/icons/HomeWork';
import { useMutation } from 'react-query';
import { UserData, UserDepartmentData } from './types';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { useHistory } from 'react-router';
import { ConfirmActionDialog } from 'components/Dialogs';
import { useForm } from 'react-hook-form';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';

export const DepartmentUserTable: FC<any> = observer(() => {
  const {
    departmentTableStore: { userDepartmentsView },
    endUserSettingsFormStore: { getPortalSettings },
    departmentUserTableStore: { createUserDepartment },
    ldapStore: { usersView, getUsers, setUsersKeywordFilter },
    notificationStore: { openNotification },
  } = useStore();
  const history = useHistory();
  const classes = useStyles();
  const [currentDepartment] = useState<any>(JSON.parse(localStorage.getItem('CurrentDepartment') as string));
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [formData, setFormData] = useState<UserDepartmentData>();
  const { handleSubmit } = useForm<UserDepartmentData>();

  const getUsersReq = useQuery('getUsersReq', () => getUsers(), {
    onError: () => openNotification('error', `Can't get users`),
  });

  // const getUserDepartmentReq = useQuery('getUserDepartment', () => getUserDepartment(currentDepartment?.dn), {
  //   onError: (error: Error) => openNotification('error', error.message),
  // });

  const createDepartmentReq = useMutation((data: UserData) => createUserDepartment(data, currentDepartment?.dn), {
    mutationKey: 'createDepartmentReq',
    onSuccess: () => {
      openNotification('success', 'Department Add Successfully');
      history.push(handleCompanyUrl('/department/users'));
    },
    onError: () => openNotification('error', `Can't add user`),
  });

  const row = useMemo(() => {
    if (!getUsersReq.isFetching) {
      return usersView.filter((user) => {
        const isUserInDepartment = userDepartmentsView.some((departmentUser) => {
          return departmentUser.uid === user.uid;
        });
        return !isUserInDepartment;
      });
    }
    return [];
  }, [getUsersReq.isFetching, usersView, userDepartmentsView]);

  const selectedRowDetails = selectedRows.map((rowId) => row.find((rowData) => rowData.id === parseInt(rowId)));
  const selectedDns = selectedRowDetails.map((rowData) => rowData?.dn).filter(Boolean);

  const handleSelectionModelChange = (selection: any) => {
    setSelectedRows(selection.selectionModel as string[]);
  };
  const portalSettingsgetReq = useQuery('portalSettingsgetReq', () => getPortalSettings(), {
    onError: () => openNotification('error', `Can't get data`),
  });
  const endUserConfigSettings = JSON.parse(localStorage.getItem('endUserConfigSettings') as string);
  const userId = endUserConfigSettings?.userId;
  const onSubmit = (formData: UserDepartmentData) => {
    const differentBase = selectedRowDetails.some((rowData) => rowData && rowData.base !== '/');
    if (differentBase) {
      setOpenConfirmDialog(true);
      setFormData(formData);
    } else {
      const newData = {
        ...formData,
        uuid: selectedDns,
      };
      createDepartmentReq.mutate(newData);
    }
  };
  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(row), [row]);
  const columns: GridColumns = [
    { field: 'sn', headerName: 'LAST', flex: 0.5 },
    { field: 'givenname', headerName: 'FIRST', flex: 0.5 },
    {
      field: 'uid',
      headerName: endUserConfigSettings?.userId === null || endUserConfigSettings?.userId === '' ? 'UID' : userId,
      flex: 0.5,
    },
    {
      field: 'base',
      headerName: 'CURRENTDEPARTMENT',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');

        return <div style={{ display: 'flex', gap: '10px' }}>{value === '/' ? 'Unassigned' : value}</div>;
      },
    },
    {
      field: 'mail',
      headerName: 'EMAIL',
      flex: 1,
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');

        return (
          <div style={{ display: 'flex', gap: '10px' }}>
            <img src={EmailIcon} alt='' />
            {value}
          </div>
        );
      },
    },
  ];

  useAppPreloader(getUsersReq.isFetching || createDepartmentReq.isLoading || portalSettingsgetReq.isFetching);
  return (
    <>
      <Grid container alignItems='center' className={classes.box}>
        <IconButton
          onClick={() => {
            history.push(handleCompanyUrl('/department/users'));
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
        <h1>
          {' '}
          Please select one or more User Accounts below to add to the Department ({currentDepartment?.ou}) selected.
        </h1>
      </Grid>
      <Grid container justify='space-between' alignItems='center' className={classes.box}>
        <IconButton aria-label='home' size='small'>
          <HomeWorkIcon />
        </IconButton>
        <Search handelSearch={setUsersKeywordFilter} />
        {!!selectedRows.length && (
          <Button variant='contained' color='primary' style={{ margin: '0 10px' }} onClick={handleSubmit(onSubmit)}>
            Move to Department
          </Button>
        )}
      </Grid>
      {getUsersReq.isFetching === false && (
        <>
          {row.length === 0 ? (
            <h1 className={classes.link}> All the users are already linked with this department. </h1>
          ) : (
            <Paper className={classes.dataGrid} elevation={0}>
              <DataGrid
                checkboxSelection
                className={classes.noBorder}
                autoHeight
                rows={row}
                columns={columns}
                pageSize={pageSize}
                rowsPerPageOptions={rowsPerPageOptions}
                pagination
                onPageChange={(params) => setPageSize(params.pageSize)}
                selectionModel={selectedRows}
                onSelectionModelChange={handleSelectionModelChange}
              />
            </Paper>
          )}
        </>
      )}
      <ConfirmActionDialog
        label='You are modify users which belong to other department.Are you sure you want to move?'
        buttonLabel='Confirm'
        gitStatus=''
        openModal={openConfirmDialog}
        action={() => {
          if (formData) {
            const newData = {
              ...formData,
              uuid: selectedDns,
            };
            createDepartmentReq.mutate(newData);
          }
        }}
        handleClose={() => {
          setOpenConfirmDialog(false);
        }}
      />
    </>
  );
});
